import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { t } from '../../i18n/nl.js';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { sendPushToClient } from '../../utils/pushUtils';
import CoachAvailabilityCalendar from '../../components/CoachAvailabilityCalendar.jsx';
import { toAmsterdamISO, amsterdamDateStr, toAmsterdamTime, todayStr, nowTimeStr } from '../../utils/dateUtils.js';

function fmtDateTime(isoStr) {
  if (!isoStr) return '—';
  return new Date(isoStr).toLocaleString('nl-NL', {
    timeZone: 'Europe/Amsterdam',
    weekday: 'short', day: 'numeric', month: 'long',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function CoachAanvragenPage() {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();

  const [aanvragen, setAanvragen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  const [altModal, setAltModal] = useState(null); // { aanvraagId, clientId, pushToken }
  const [altDatum, setAltDatum] = useState('');
  const [altTijd, setAltTijd] = useState('');
  const [altNotitie, setAltNotitie] = useState('');
  const [altSaving, setAltSaving] = useState(false);

  const [conflictModal, setConflictModal] = useState(null); // { conflicts, onConfirm }

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('appointment_requests')
        .select(`
          id, requested_at, duration_minutes, note, status, credit_cost,
          reschedule_proposed_at, reschedule_note,
          client:profiles!appointment_requests_client_id_fkey(id, full_name),
          location:locations(id, name)
        `)
        .eq('coach_id', user.id)
        .in('status', ['pending', 'rescheduled'])
        .order('requested_at', { ascending: true });
      if (error) throw error;
      setAanvragen(data || []);
    } catch (e) {
      showError(t('ptAanvragen.foutLaden') + e.message);
    } finally {
      setLoading(false);
    }
  }

  async function doAccept(item) {
    setConflictModal(null);
    setActingId(item.id);
    try {
      const { data: rpcResult, error: rpcErr } = await supabase.rpc('accept_appointment_request', {
        p_aanvraag_id: item.id,
      });
      if (rpcErr) throw rpcErr;
      if (rpcResult?.error) throw new Error(rpcResult.message || rpcResult.error);

      {
        const dateLabel = new Date(item.requested_at).toLocaleDateString('nl-NL', {
          timeZone: 'Europe/Amsterdam', day: 'numeric', month: 'long',
        });
        await sendPushToClient(supabase, user.id, item.client?.id, 'pt_appointments',
          t('ptAanvragen.pushAccepteerdTitel'),
          t('ptAanvragen.pushAccepteerdBody').replace('{datum}', dateLabel),
        ).catch(() => {});
      }

      showSuccess(t('ptAanvragen.geaccepteerd'));
      load();
    } catch (e) {
      showError(t('ptAanvragen.foutBijwerken') + e.message);
    } finally {
      setActingId(null);
    }
  }

  async function handleAccept(item) {
    setActingId(item.id);
    try {
      const startsAt = item.reschedule_proposed_at || item.requested_at;
      const endsAt = new Date(new Date(startsAt).getTime() + (item.duration_minutes || 60) * 60000).toISOString();
      const [{ data: conflicts, error: confErr }, { data: externalConflicts, error: extErr }] = await Promise.all([
        supabase.rpc('check_coach_time_conflicts', {
          p_coach_id: user.id,
          p_starts_at: startsAt,
          p_ends_at: endsAt,
          p_exclude_id: null,
        }),
        supabase.rpc('check_coach_external_calendar_conflicts', {
          p_coach_id: user.id,
          p_starts_at: startsAt,
          p_ends_at: endsAt,
        }),
      ]);
      if (confErr) throw confErr;
      if (extErr) throw extErr;
      // Externe agenda-items (ICS-sync) zijn altijd overrulebaar: ze zijn niet
      // per definitie fysieke afspraken (bv. all-day reminders/verjaardagen).
      const softExternal = (externalConflicts || [])
        .map(c => ({ ...c, conflict_title: c.conflict_title || t('conflicten.privateAgendaBusy') }));
      const allConflicts = [...(conflicts || []), ...softExternal];
      if (allConflicts.length > 0) {
        setActingId(null);
        setConflictModal({ conflicts: allConflicts, onConfirm: () => doAccept(item) });
        return;
      }
      await doAccept(item);
    } catch (e) {
      showError(t('ptAanvragen.foutBijwerken') + e.message);
      setActingId(null);
    }
  }

  async function handleDecline(item) {
    setActingId(item.id);
    try {
      const { error } = await supabase
        .from('appointment_requests')
        .update({ status: 'declined' })
        .eq('id', item.id);
      if (error) throw error;

      {
        const dateLabel = new Date(item.requested_at).toLocaleDateString('nl-NL', {
          timeZone: 'Europe/Amsterdam', day: 'numeric', month: 'long',
        });
        await sendPushToClient(supabase, user.id, item.client?.id, 'pt_appointments',
          t('ptAanvragen.pushAfgewezenTitel'),
          t('ptAanvragen.pushAfgewezenBody').replace('{datum}', dateLabel),
        ).catch(() => {});
      }

      showSuccess(t('ptAanvragen.afgewezen'));
      load();
    } catch (e) {
      showError(t('ptAanvragen.foutBijwerken') + e.message);
    } finally {
      setActingId(null);
    }
  }

  // Onderdeel 3 (2026-07-26): startte op vandaag/nu i.p.v. op het aangevraagde
  // moment — zelfde bug als in de app (CoachAanvragenScreen.openAltModal).
  function openAltModal(item) {
    const basis = item.reschedule_proposed_at || item.requested_at;
    setAltDatum(basis ? amsterdamDateStr(basis) : todayStr());
    setAltTijd(basis ? toAmsterdamTime(basis) : nowTimeStr());
    setAltNotitie('');
    setAltModal({ aanvraagId: item.id, clientId: item.client?.id, durationMinutes: item.duration_minutes });
  }

  async function handleSendAlternative() {
    if (!altDatum) { showError(t('ptAanvragen.datumVerplicht')); return; }
    if (!altTijd) { showError(t('ptAanvragen.tijdVerplicht')); return; }
    setAltSaving(true);
    try {
      // Via toAmsterdamISO i.p.v. new Date(...) op de browsertijdzone: het
      // voorstel is een Amsterdams tijdstip, ongeacht waar de coach inlogt.
      const rescheduleProposedAt = toAmsterdamISO(altDatum, altTijd);

      const { error } = await supabase
        .from('appointment_requests')
        .update({
          status: 'rescheduled',
          reschedule_proposed_at: rescheduleProposedAt,
          reschedule_note: altNotitie || null,
        })
        .eq('id', altModal.aanvraagId);
      if (error) throw error;

      await sendPushToClient(supabase, user.id, altModal.clientId, 'pt_appointments',
        t('ptAanvragen.pushAlternatiefTitel'),
        t('ptAanvragen.pushAlternatiefBody'),
      ).catch(() => {});

      showSuccess(t('ptAanvragen.alternatiefVerstuurd'));
      setAltModal(null);
      load();
    } catch (e) {
      showError(t('ptAanvragen.foutBijwerken') + e.message);
    } finally {
      setAltSaving(false);
    }
  }

  const badgeStyle = {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 99,
    fontSize: 12,
    fontWeight: 600,
  };

  return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 24 }}>{t('ptAanvragen.titel')}</h2>

      {loading ? (
        <p style={{ color: 'var(--color-text-secondary)' }}>{t('ptAanvragen.laden')}</p>
      ) : aanvragen.length === 0 ? (
        <div style={{
          padding: 32, textAlign: 'center', background: 'var(--color-card)',
          borderRadius: 'var(--radius)', border: '1px solid var(--color-border)',
        }}>
          <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>{t('ptAanvragen.geenAanvragen')}</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {aanvragen.map(item => {
            const isPending = item.status === 'pending';
            const isRescheduled = item.status === 'rescheduled';
            const isBusy = actingId === item.id;

            return (
              <div key={item.id} style={{
                background: 'var(--color-card)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                padding: 20,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <span style={{ fontWeight: 700, fontSize: 16 }}>
                    {item.client?.full_name || '—'}
                  </span>
                  <span style={{
                    ...badgeStyle,
                    background: isRescheduled ? '#fff3e0' : '#e3f2fd',
                    color: isRescheduled ? '#e65100' : '#1565c0',
                  }}>
                    {isRescheduled ? t('ptAanvragen.statusRescheduled') : t('ptAanvragen.statusPending')}
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px', marginBottom: 12 }}>
                  <div>
                    <span style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>{t('ptAanvragen.datumLabel')}: </span>
                    <span style={{ fontWeight: 500 }}>
                      {fmtDateTime(isRescheduled ? item.reschedule_proposed_at : item.requested_at)}
                    </span>
                  </div>
                  <div>
                    <span style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>{t('ptAanvragen.duurLabel')}: </span>
                    <span>{t('ptAanvragen.minutenSuffix').replace('{{count}}', item.duration_minutes)}</span>
                  </div>
                  <div>
                    <span style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>{t('ptAanvragen.locatieLabel')}: </span>
                    <span>{item.location?.name || '—'}</span>
                  </div>
                </div>

                {item.note && (
                  <p style={{ fontStyle: 'italic', color: 'var(--color-text-secondary)', fontSize: 13, margin: '0 0 8px' }}>
                    &ldquo;{item.note}&rdquo;
                  </p>
                )}
                {isRescheduled && item.reschedule_note && (
                  <p style={{ fontStyle: 'italic', color: '#e65100', fontSize: 13, margin: '0 0 8px' }}>
                    {t('ptAanvragen.rescheduledNota')}: &ldquo;{item.reschedule_note}&rdquo;
                  </p>
                )}

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  {isPending && (
                    <>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleAccept(item)}
                        disabled={isBusy}
                      >
                        {t('ptAanvragen.accepteerKnop')}
                      </button>
                      <button
                        className="btn btn-sm"
                        style={{ background: '#c62828', color: '#fff', border: 'none' }}
                        onClick={() => handleDecline(item)}
                        disabled={isBusy}
                      >
                        {t('ptAanvragen.afwijzenKnop')}
                      </button>
                      <button
                        className="btn btn-sm"
                        style={{ background: '#e65100', color: '#fff', border: 'none' }}
                        onClick={() => openAltModal(item)}
                        disabled={isBusy}
                      >
                        {t('ptAanvragen.alternatiefKnop')}
                      </button>
                    </>
                  )}
                  {isRescheduled && (
                    <>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleAccept(item)}
                        disabled={isBusy}
                      >
                        {t('ptAanvragen.bevestigenKnop')}
                      </button>
                      <button
                        className="btn btn-sm"
                        style={{ background: '#c62828', color: '#fff', border: 'none' }}
                        onClick={() => handleDecline(item)}
                        disabled={isBusy}
                      >
                        {t('ptAanvragen.afwijzenKnop')}
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Alternatief modal */}
      {altModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h3 className="modal-title">{t('ptAanvragen.alternatiefTitel')}</h3>
              <button className="modal-close" onClick={() => setAltModal(null)}>✕</button>
            </div>

            <label style={{ display: 'block', marginBottom: 12 }}>
              <span style={{ fontSize: 13, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                {t('ptAanvragen.nieuweDatumLabel')}
              </span>
              <input
                type="date"
                className="form-input"
                value={altDatum}
                onChange={e => setAltDatum(e.target.value)}
              />
            </label>

            <label style={{ display: 'block', marginBottom: 12 }}>
              <span style={{ fontSize: 13, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                {t('ptAanvragen.nieuwTijdLabel')}
              </span>
              <input
                type="time"
                className="form-input"
                value={altTijd}
                onChange={e => setAltTijd(e.target.value)}
              />
            </label>

            <CoachAvailabilityCalendar
              coachId={user.id}
              baseDateStr={altDatum}
              selectedStartsAt={altDatum && altTijd ? toAmsterdamISO(altDatum, altTijd) : null}
              durationMinutes={altModal.durationMinutes || 60}
              onSelectSlot={(dateStr, timeStr) => {
                setAltDatum(dateStr);
                setAltTijd(timeStr);
              }}
            />

            <label style={{ display: 'block', marginBottom: 20 }}>
              <span style={{ fontSize: 13, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                {t('ptAanvragen.notitieLabel')}
              </span>
              <textarea
                className="form-input"
                rows={3}
                placeholder={t('ptAanvragen.notitiePlaceholder')}
                value={altNotitie}
                onChange={e => setAltNotitie(e.target.value)}
                style={{ resize: 'vertical' }}
              />
            </label>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setAltModal(null)} disabled={altSaving}>
                {t('ptAanvragen.annuleren')}
              </button>
              <button className="btn btn-primary btn-sm" onClick={handleSendAlternative} disabled={altSaving}>
                {altSaving ? '…' : t('ptAanvragen.verstuurKnop')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Conflicten modal */}
      {conflictModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <h3 className="modal-title">{t('ptAanvragen.conflictenTitel')}</h3>
              <button className="modal-close" onClick={() => setConflictModal(null)}>✕</button>
            </div>
            <p style={{ color: 'var(--color-text-muted)', fontSize: 14, margin: '0 0 16px' }}>
              {t('ptAanvragen.conflicten')}
            </p>
            {(conflictModal.conflicts || []).map((c, i) => (
              <div key={i} style={{
                padding: '8px 12px', marginBottom: 8,
                background: '#fff3e0', borderRadius: 6, fontSize: 13,
              }}>
                <strong>{c.conflict_title}</strong>
                <br />
                <span style={{ color: 'var(--color-text-muted)' }}>
                  {new Date(c.conflict_starts_at).toLocaleString('nl-NL', {
                    timeZone: 'Europe/Amsterdam', weekday: 'short',
                    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                  })}
                </span>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setConflictModal(null)}>
                {t('ptAanvragen.annuleren')}
              </button>
              <button
                className="btn btn-sm"
                style={{ background: '#c62828', color: '#fff', border: 'none' }}
                onClick={() => conflictModal.onConfirm()}
              >
                {t('ptAanvragen.tochAccepteren')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
